import React, { useState, useEffect, useCallback, useMemo } from 'react';
import useForm from 'hooks/useLocalizedSimpleForm';
import { serializeActionlink, deserializeActionlink, generateActionlinkFieldKey } from 'helpers/push.helper';
import { PushValidators } from 'validators/ExperienceCanvas/push.validator';
import { PushFormClassPrefix as CLASS_PREFIX } from 'components/ExperienceCanvas/constants';
import { PushUniversalAction } from 'components/ExperienceCanvas/types';
import { FlightButton, FlightCheckbox } from '@flybits/design-system';
import ActionTypeSelector from 'components/ExperienceCanvas/TouchpointEditor/TouchpointTimelineBox/PushEditorBox/ActionTypeSelector';
import ActionLinkFieldControl from 'components/ExperienceCanvas/TouchpointEditor/TouchpointTimelineBox/PushEditorBox/ActionLinkFieldControl';
import ContentIdFieldRow from 'components/ExperienceCanvas/TouchpointEditor/TouchpointTimelineBox/PushEditorBox/ContentIdDropdown/ContentIdFieldRow';
import useContentIdSelector from 'components/ExperienceCanvas/TouchpointEditor/TouchpointTimelineBox/PushEditorBox/ContentIdDropdown/useContentIdSelector';
import { PushDraft, PushDraftCallbacks } from '../pushDraft.types';

type ActionLinkFormProps = Pick<PushDraftCallbacks, 'onChangePushField'> & {
  pushDraft: PushDraft;
  activeLang: string;
  defaultLang: string;
};

const ActionLinkForm = ({ pushDraft, activeLang, defaultLang, onChangePushField }: ActionLinkFormProps) => {
  const deserializedActionLink = useMemo(
    () => deserializeActionlink(pushDraft.actionLinkScheme, activeLang),
    [pushDraft.actionLinkScheme, activeLang],
  );

  const { formData, updateField, toggleError } = useForm({
    data: {
      ...deserializedActionLink.formFields,
      bypassStrictValidation: { value: false },
    },
    validators: PushValidators,
    lang: activeLang,
    defaultLang,
  });

  const [selectedActionType, setSelectedActionType] = useState(deserializedActionLink.actionType);
  const [isActionSelectorOpen, setIsActionSelectorOpen] = useState(false);
  const [isContentIdSelectorOpen, setIsContentIdSelectorOpen] = useState(false);
  const [selectedContentDisplayName, setSelectedContentDisplayName] = useState('');
  const [lastSelectedContentId, setLastSelectedContentId] = useState('');

  const handleContentIdDropdownClose = useCallback(() => {
    setIsContentIdSelectorOpen(false);
  }, []);

  const contentIdSelector = useContentIdSelector({
    isOpen: isContentIdSelectorOpen,
    onClose: handleContentIdDropdownClose,
  });

  const persistActionLink = useCallback(
    (actionType: PushUniversalAction, currentFormData: typeof formData) => {
      onChangePushField('actionLinkScheme', activeLang, serializeActionlink(actionType, currentFormData));
    },
    [activeLang, onChangePushField],
  );

  const handleChangeActionType = (actionType: PushUniversalAction) => {
    setSelectedActionType(actionType);
    setIsActionSelectorOpen(false);
    onChangePushField('actionLinkType', {
      displayName: actionType.name,
      val: 'UNIVERSAL_ACTION',
      validationPrefix: actionType.prefix,
    });
    persistActionLink(actionType, formData);
  };

  const onChangeBypass = () => {
    const currentValue = formData.bypassStrictValidation.value;

    updateField('bypassStrictValidation', !currentValue);
    selectedActionType.fields.forEach((field, idx) => {
      const fieldKey = generateActionlinkFieldKey(selectedActionType, idx);
      toggleError(fieldKey);
    });
  };

  useEffect(() => {
    setSelectedActionType(deserializedActionLink.actionType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeLang]);

  useEffect(() => {
    persistActionLink(selectedActionType, formData);
  }, [activeLang, formData, selectedActionType, persistActionLink]);

  const isDetailsAction = selectedActionType.prefix === 'details:';

  const detailsContentIdKey = useMemo(() => {
    if (!isDetailsAction) return '';
    const contentIdFieldIdx = selectedActionType.fields.findIndex((field) => field.key === 'contentId');
    return generateActionlinkFieldKey(selectedActionType, contentIdFieldIdx >= 0 ? contentIdFieldIdx : 0);
  }, [isDetailsAction, selectedActionType]);

  const handleSelectContentId = useCallback(
    (contentId: string, contentName?: string) => {
      if (!detailsContentIdKey || !contentId) return;

      updateField(detailsContentIdKey, contentId);
      setLastSelectedContentId(contentId);
      setSelectedContentDisplayName(contentName || contentId);
      toggleError(detailsContentIdKey);
      contentIdSelector.close();
    },
    [contentIdSelector, detailsContentIdKey, toggleError, updateField],
  );

  useEffect(() => {
    if (!detailsContentIdKey) {
      setSelectedContentDisplayName('');
      setLastSelectedContentId('');
      return;
    }

    const contentId = formData[detailsContentIdKey]?.value?.trim() ?? '';
    if (!contentId) {
      setSelectedContentDisplayName('');
      setLastSelectedContentId('');
    }
  }, [detailsContentIdKey, formData]);

  const valueHeader = (
    <div
      className={`${CLASS_PREFIX}__row ${CLASS_PREFIX}__value-header ${
        isDetailsAction ? `${CLASS_PREFIX}__value-header--with-dropdown` : ''
      }`}
    >
      <div className={`${CLASS_PREFIX}__label`}>
        <p className={`${CLASS_PREFIX}__label-primary`}>Enter Universal Action&apos;s Value</p>
      </div>
      {isDetailsAction && (
        <FlightButton
          ariaLabel="select content id"
          label="Select Content ID"
          theme="link"
          onClick={() => setIsContentIdSelectorOpen((prev) => !prev)}
        />
      )}
    </div>
  );

  const actionFields = selectedActionType.fields.map((fieldMetadata, idx) => {
    const fieldKey = generateActionlinkFieldKey(selectedActionType, idx);
    const fieldKeyProp = `push-at-field-${selectedActionType.name}-${idx}`;

    if (isDetailsAction && fieldMetadata.key === 'contentId') {
      return (
        <ContentIdFieldRow
          key={fieldKeyProp}
          fieldKey={fieldKey}
          fieldMetadata={fieldMetadata}
          field={formData[fieldKey]}
          updateField={updateField}
          toggleError={toggleError}
          isDropdownOpen={isContentIdSelectorOpen}
          contentIdSelector={contentIdSelector}
          onSelectContentId={handleSelectContentId}
          selectedContentDisplayName={selectedContentDisplayName}
          lastSelectedContentId={lastSelectedContentId}
        />
      );
    }

    return (
      <div className={`${CLASS_PREFIX}__row`} key={fieldKeyProp}>
        <ActionLinkFieldControl
          fieldKey={fieldKey}
          fieldMetadata={fieldMetadata}
          field={formData[fieldKey]}
          updateField={updateField}
          toggleError={toggleError}
        />
      </div>
    );
  });

  return (
    <div className={`${CLASS_PREFIX}__container`}>
      <div className={`${CLASS_PREFIX}__row`}>
        <div className={`${CLASS_PREFIX}__label`}>
          <p className={`${CLASS_PREFIX}__label-primary`}>Select Universal Action</p>
        </div>
      </div>
      <ActionTypeSelector
        selectedActionType={selectedActionType}
        isOpen={isActionSelectorOpen}
        onOpenChange={setIsActionSelectorOpen}
        onSelect={handleChangeActionType}
      />
      {isDetailsAction ? (
        <div ref={contentIdSelector.scopeRef} className={`${CLASS_PREFIX}__content-id-dropdown-scope`}>
          {valueHeader}
          {actionFields}
        </div>
      ) : (
        <>
          {valueHeader}
          {actionFields}
        </>
      )}
      <div className={`${CLASS_PREFIX}__actionlink__preview`}>
        {`Action Preview: ${serializeActionlink(selectedActionType, formData)}`}
      </div>
      <div className={`${CLASS_PREFIX}__row`}>
        <div className={`${CLASS_PREFIX}__label`}>
          <p className={`${CLASS_PREFIX}__label-primary`}>Allow custom value for {selectedActionType.name}</p>
        </div>
        <FlightCheckbox
          className={`${CLASS_PREFIX}__right`}
          checkState={formData.bypassStrictValidation.value ? 'SELECTED' : 'UNSELECTED'}
          onSelect={onChangeBypass}
        />
      </div>
    </div>
  );
};

export default ActionLinkForm;
